import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import ThemePreviewModal from '../components/ThemePreviewModal';
import { ThemeContext } from '../context/ThemeContext';
import '../index.css';

const Shop = () => {
  const { applyCursor } = useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState('cosmetics');
  const [activeCosmeticCategory, setActiveCosmeticCategory] = useState('cursors');
  const [shopItems, setShopItems] = useState([]);
  const [cosmetics, setCosmetics] = useState({});
  const [equippedCosmetics, setEquippedCosmetics] = useState({});
  const [activityScore, setActivityScore] = useState({ total: 0, spent: 0, available: 0 });
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [previewTheme, setPreviewTheme] = useState(null);
  const [giveawayStats, setGiveawayStats] = useState({ hosted: 0, claimed: 0, canClaim: false });
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    fetchShopData();
    fetchGiveawayStats();
    fetchCosmetics();
  }, []);

  const fetchShopData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.get('/api/shop/items', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShopItems(response.data.data.items);
      setActivityScore(response.data.data.activityScore);
      setIsAdmin(response.data.data.isAdmin);
    } catch (error) {
      console.error('Error fetching shop data:', error);
      setError('Failed to load shop items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCosmetics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/shop/cosmetics', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCosmetics(response.data.data.cosmetics);
      setEquippedCosmetics(response.data.data.equipped);
      setActivityScore(response.data.data.activityScore);
      setIsAdmin(response.data.data.isAdmin);
    } catch (error) {
      console.error('Error fetching cosmetics:', error);
    }
  };

  const fetchGiveawayStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/shop/giveaway-rewards', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGiveawayStats(response.data.data);
    } catch (error) {
      console.error('Error fetching giveaway stats:', error);
    }
  };

  const handlePurchaseTheme = async (themeId, themeName, price) => {
    if (!window.confirm(`Purchase ${themeName} for ${price} activity points?`)) {
      return;
    }

    try {
      setPurchasing(themeId);

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/shop/purchase', 
        { themeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(response.data.message);
      await fetchShopData();
      window.location.reload();
    } catch (error) {
      console.error('Error purchasing theme:', error);
      alert(error.response?.data?.message || 'Failed to purchase theme');
    } finally {
      setPurchasing(null);
    }
  };

  const handlePurchaseCosmetic = async (itemId, itemName, price, category) => {
    if (!window.confirm(`Purchase ${itemName} for ${price} activity points?`)) {
      return;
    }

    try {
      setPurchasing(itemId);

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/shop/cosmetics/purchase',
        { itemId, category },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(response.data.message);
      await fetchCosmetics();
    } catch (error) {
      console.error('Error purchasing cosmetic:', error);
      alert(error.response?.data?.message || 'Failed to purchase cosmetic');
    } finally {
      setPurchasing(null);
    }
  };

  const handleEquipCosmetic = async (itemId, category) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/shop/cosmetics/equip',
        { itemId, category },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchCosmetics();
      if (category === 'cursors') {
        applyCursor(itemId);
      }
      alert(response.data.message);
    } catch (error) {
      console.error('Error equipping cosmetic:', error);
      alert(error.response?.data?.message || 'Failed to equip cosmetic');
    }
  };

  const handleUnequipCosmetic = async (itemId, category) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/shop/cosmetics/unequip',
        { itemId, category },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchCosmetics();
      if (category === 'cursors') {
        applyCursor(null);
      }
      alert(response.data.message);
    } catch (error) {
      console.error('Error unequipping cosmetic:', error);
      alert(error.response?.data?.message || 'Failed to unequip cosmetic');
    }
  };

  const handleClaimGiveawayReward = async () => {
    if (!window.confirm('Claim 1000 activity points for hosting 10 giveaways?')) {
      return;
    }

    try {
      setClaiming(true);

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/shop/claim-giveaway-reward', 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(response.data.message);
      await fetchShopData();
      await fetchGiveawayStats();
      await fetchCosmetics();
    } catch (error) {
      console.error('Error claiming reward:', error);
      alert(error.response?.data?.message || 'Failed to claim reward');
    } finally {
      setClaiming(false);
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'legendary':
        return '#FFD700'; // Gold
      case 'epic':
        return '#9B59B6'; // Purple
      case 'rare':
        return '#3498DB'; // Blue
      case 'common':
        return '#95A5A6'; // Gray
      case 'exclusive':
        return '#FFD700'; // Gold (theme rarity)
      case 'premium':
        return '#9B59B6'; // Purple (theme rarity)
      case 'enhanced':
        return '#3498DB'; // Blue (theme rarity)
      case 'basic':
        return '#95A5A6'; // Gray (theme rarity)
      default:
        return '#95A5A6';
    }
  };

  const getCategoryName = (category) => {
    const names = {
      avatarFrames: 'Avatar Frames',
      badges: 'Profile Badges',
      cardBackgrounds: 'Card Backgrounds',
      usernameEffects: 'Username Effects',
      cardBorders: 'Card Borders',
      cursors: 'Custom Cursors'
    };
    return names[category] || category;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      avatarFrames: '🖼️',
      badges: '🏅',
      cardBackgrounds: '🎨',
      usernameEffects: '✨',
      cardBorders: '📐',
      cursors: '🖱️'
    };
    return icons[category] || '🎁';
  };

  if (loading) {
    return (
      <div style={{
        padding: '80px 20px',
        textAlign: 'center',
        minHeight: '100vh',
        color: 'var(--text)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start'
      }}>
        <div className="loading-spinner"></div>
        <div style={{ fontSize: '20px' }}>Loading shop...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '80px 20px',
        textAlign: 'center',
        minHeight: '100vh',
        color: 'var(--error-text)'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <div style={{ fontSize: '24px' }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '80px 20px 40px',
      maxWidth: '1400px',
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      {/* Modern Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '50px'
      }}>
        <div style={{ 
          fontSize: '72px', 
          marginBottom: '10px',
          filter: 'drop-shadow(0 4px 12px rgba(102, 126, 234, 0.3))'
        }}>
          🛍️
        </div>
        <h1 style={{
          fontSize: '56px',
          margin: '0 0 20px 0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 'bold',
          letterSpacing: '-1px'
        }}>
          Shop
        </h1>
        <p style={{
          fontSize: '20px',
          color: 'var(--text-secondary)',
          margin: '0 0 32px 0',
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          Customize your profile and unlock exclusive items
        </p>

        {/* Activity Score Display - Modern Card */}
        <div style={{
          display: 'inline-block',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          padding: '24px 48px',
          borderRadius: '20px',
          border: '2px solid var(--border)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginBottom: '8px',
            textTransform: 'uppercase',
            fontWeight: '700',
            letterSpacing: '2px'
          }}>
            💰 Your Points
          </div>
          <div style={{
            fontSize: '42px',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '4px'
          }}>
            {(activityScore.available || 0).toFixed(0)}
          </div>
          <div style={{
            fontSize: '13px',
            color: 'var(--text-secondary)'
          }}>
            Total: {(activityScore.total || 0).toFixed(0)} pts
          </div>
        </div>

        {isAdmin && (
          <div style={{
            marginTop: '20px',
            padding: '12px 32px',
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 165, 0, 0.1) 100%)',
            border: '2px solid var(--warning-text)',
            borderRadius: '16px',
            display: 'inline-block',
            color: 'var(--warning-text)',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            👑 Admin Access - All Items Unlocked
          </div>
        )}
      </div>

      {/* Main Tabs */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '40px',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'cosmetics', label: 'Cosmetics', icon: '✨' },
          { id: 'themes', label: 'Themes', icon: '🎨' },
          { id: 'memberships', label: 'Memberships', icon: '⭐' },
          { id: 'rewards', label: 'Host Rewards', icon: '🎁' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '14px',
              border: activeTab === tab.id ? 'none' : '2px solid var(--border)',
              cursor: 'pointer',
              background: activeTab === tab.id 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                : 'var(--card-bg)',
              color: activeTab === tab.id ? 'white' : 'var(--text)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: activeTab === tab.id ? '0 8px 20px rgba(102, 126, 234, 0.4)' : 'none',
              transform: activeTab === tab.id ? 'translateY(-2px)' : 'translateY(0)'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.target.style.backgroundColor = 'var(--hover-bg)';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.target.style.backgroundColor = 'var(--card-bg)';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            <span style={{ marginRight: '8px' }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Cosmetics Tab */}
      {activeTab === 'cosmetics' && (
        <div>
          {/* Cosmetic Category Pills */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '40px',
            flexWrap: 'wrap'
          }}>
            {Object.keys(cosmetics).map(category => (
              <button
                key={category}
                onClick={() => setActiveCosmeticCategory(category)}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '12px',
                  border: activeCosmeticCategory === category ? 'none' : '2px solid var(--border)',
                  cursor: 'pointer',
                  background: activeCosmeticCategory === category 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'var(--card-bg)',
                  color: activeCosmeticCategory === category ? 'white' : 'var(--text-secondary)',
                  transition: 'all 0.2s ease',
                  boxShadow: activeCosmeticCategory === category ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeCosmeticCategory !== category) {
                    e.target.style.backgroundColor = 'var(--hover-bg)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeCosmeticCategory !== category) {
                    e.target.style.backgroundColor = 'var(--card-bg)';
                  }
                }}
              >
                {getCategoryIcon(category)} {getCategoryName(category)}
              </button>
            ))}
          </div>

          {/* Cosmetic Items Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '40px'
          }}>
            {cosmetics[activeCosmeticCategory]?.map(item => {
              const isPurchased = item.purchased;
              const isEquipped = item.equipped;
              const canAfford = item.canAfford;
              const isSupportersOnly = item.supportersOnly;
              const hasAccess = item.hasAccess;

              return (
                <div
                  key={item.id}
                  style={{
                    background: 'var(--card-bg)',
                    borderRadius: '20px',
                    padding: '24px',
                    border: isEquipped 
                      ? '3px solid #4CAF50'
                      : isPurchased
                        ? '2px solid #4CAF50'
                        : '2px solid var(--border)',
                    boxShadow: isEquipped
                      ? '0 8px 32px rgba(76, 175, 80, 0.4)'
                      : '0 4px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = isEquipped 
                      ? '0 12px 40px rgba(76, 175, 80, 0.5)'
                      : '0 8px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = isEquipped
                      ? '0 8px 32px rgba(76, 175, 80, 0.4)'
                      : '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                >
                  {/* Top Badges */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginBottom: '16px',
                    minHeight: '24px',
                    gap: '8px'
                  }}>
                    {isEquipped && (
                      <div style={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        padding: '6px 14px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        ⚡ Equipped
                      </div>
                    )}
                    {isPurchased && !isEquipped && (
                      <div style={{
                        backgroundColor: 'var(--success-text)',
                        color: 'white',
                        padding: '6px 14px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        ✓ Owned
                      </div>
                    )}
                    {isSupportersOnly && !isPurchased && (
                      <div style={{
                        backgroundColor: '#FFD700',
                        color: '#000',
                        padding: '6px 14px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        ⭐ Supporters Only
                      </div>
                    )}
                  </div>

                  {/* Item Preview */}
                  <div style={{
                    fontSize: '64px',
                    textAlign: 'center',
                    margin: '20px 0',
                    filter: !hasAccess && !isPurchased ? 'grayscale(80%)' : 'none',
                    opacity: !hasAccess && !isPurchased ? 0.4 : 1,
                    transition: 'all 0.3s ease',
                    minHeight: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent'
                  }}>
                    {item.preview && item.preview.startsWith('/') ? (
                      <img 
                        src={item.preview} 
                        alt={item.name}
                        style={{
                          maxWidth: '120px',
                          maxHeight: '120px',
                          objectFit: 'contain',
                          background: 'transparent'
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '80px' }}>{item.preview}</span>
                    )}
                  </div>

                  {/* Item Name */}
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: 'var(--text)',
                    margin: '0 0 8px 0',
                    textAlign: 'center'
                  }}>
                    {item.name}
                  </h3>

                  {/* Description */}
                  <p style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    margin: '0 0 20px 0',
                    textAlign: 'center',
                    lineHeight: '1.5',
                    minHeight: '40px'
                  }}>
                    {item.description}
                  </p>

                  {/* Price Tag */}
                  {!isPurchased && (
                    <div style={{
                      background: `linear-gradient(135deg, ${getRarityColor(item.rarity)}20 0%, ${getRarityColor(item.rarity)}10 100%)`,
                      padding: '12px',
                      borderRadius: '12px',
                      marginBottom: '16px',
                      textAlign: 'center',
                      border: `1px solid ${getRarityColor(item.rarity)}30`
                    }}>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: getRarityColor(item.rarity)
                      }}>
                        {item.price} pts
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {!isPurchased ? (
                    isSupportersOnly ? (
                      hasAccess ? (
                        <button
                          onClick={() => handlePurchaseCosmetic(item.id, item.name, item.price, activeCosmeticCategory)}
                          disabled={purchasing === item.id}
                          style={{
                            width: '100%',
                            padding: '14px',
                            fontSize: '15px',
                            fontWeight: 'bold',
                            borderRadius: '12px',
                            border: 'none',
                            cursor: 'pointer',
                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                            color: '#000',
                            transition: 'all 0.3s ease',
                            opacity: purchasing === item.id ? 0.7 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (purchasing !== item.id) {
                              e.target.style.transform = 'scale(1.05)';
                              e.target.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          {purchasing === item.id ? '⏳ Claiming...' : '⭐ Claim Free'}
                        </button>
                      ) : (
                        <div style={{
                          width: '100%',
                          padding: '14px',
                          fontSize: '15px',
                          fontWeight: 'bold',
                          borderRadius: '12px',
                          border: '2px solid var(--warning-text)',
                          backgroundColor: 'var(--warning-bg)',
                          color: 'var(--warning-text)',
                          textAlign: 'center'
                        }}>
                          🔒 Become a Supporter
                        </div>
                      )
                    ) : (
                      <button
                        onClick={() => handlePurchaseCosmetic(item.id, item.name, item.price, activeCosmeticCategory)}
                        disabled={!canAfford || purchasing === item.id}
                        style={{
                          width: '100%',
                          padding: '14px',
                          fontSize: '15px',
                          fontWeight: 'bold',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: canAfford ? 'pointer' : 'not-allowed',
                          background: canAfford 
                            ? `linear-gradient(135deg, ${getRarityColor(item.rarity)} 0%, ${getRarityColor(item.rarity)}CC 100%)`
                            : 'var(--btn-secondary)',
                          color: 'white',
                          transition: 'all 0.3s ease',
                          opacity: purchasing === item.id ? 0.7 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (canAfford && purchasing !== item.id) {
                            e.target.style.transform = 'scale(1.05)';
                            e.target.style.boxShadow = `0 6px 20px ${getRarityColor(item.rarity)}60`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (canAfford) {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {purchasing === item.id 
                          ? '⏳ Purchasing...' 
                          : canAfford 
                            ? '🛒 Purchase' 
                            : '🔒 Insufficient Points'}
                      </button>
                    )
                  ) : isEquipped ? (
                    <button
                      onClick={() => handleUnequipCosmetic(item.id, activeCosmeticCategory)}
                      style={{
                        width: '100%',
                        padding: '14px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        borderRadius: '12px',
                        border: '2px solid var(--warning-text)',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        color: 'var(--warning-text)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--warning-bg)';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      🔓 Unequip
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEquipCosmetic(item.id, activeCosmeticCategory)}
                      style={{
                        width: '100%',
                        padding: '14px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: 'white',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 6px 20px rgba(34, 197, 94, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      ⚡ Equip Now
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Info Section */}
          <div style={{
            padding: '28px',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            borderRadius: '20px',
            border: '2px solid var(--border)'
          }}>
            <h3 style={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: 'var(--text)',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>ℹ️</span> How Cosmetics Work
            </h3>
            <ul style={{
              color: 'var(--text-secondary)',
              fontSize: '15px',
              lineHeight: '1.8',
              paddingLeft: '24px',
              margin: 0
            }}>
              <li>Purchase cosmetics using your activity points earned from site activities</li>
              <li>Equip items to customize how your profile card appears to others</li>
              <li>Mix and match items from different categories to create your unique look</li>
              <li>You can equip: 1 avatar frame, up to 3 badges, 1 background, 1 username effect, and 1 card border</li>
              <li>Rarity affects visual quality: Common → Rare → Epic → Legendary</li>
              <li>Unequip and re-equip items anytime - they're yours forever once purchased!</li>
            </ul>
          </div>
        </div>
      )}

      {/* Themes Tab - Existing content */}
      {activeTab === 'themes' && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '28px',
            marginTop: '40px'
          }}>
            {shopItems.map(item => {
              const isPurchased = item.purchased || item.unlockedByAdmin;
              const canAfford = item.canAfford || item.unlockedByAdmin;

              return (
                <div
                  key={item.id}
                  style={{
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '20px',
                    padding: '28px',
                    border: isPurchased 
                      ? '3px solid #4CAF50' 
                      : '2px solid var(--border)',
                    boxShadow: isPurchased 
                      ? '0 8px 24px rgba(76, 175, 80, 0.3)'
                      : '0 4px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    if (!isPurchased) {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isPurchased) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                    }
                  }}
                >
                  {/* Badges */}
                  {isPurchased && (
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      left: '16px',
                      backgroundColor: 'var(--success-text)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      ✓ Owned
                    </div>
                  )}

                  <div style={{
                    fontSize: '72px',
                    textAlign: 'center',
                    margin: '20px 0',
                    filter: !isPurchased && !canAfford ? 'grayscale(100%)' : 'none',
                    opacity: !isPurchased && !canAfford ? 0.5 : 1
                  }}>
                    {item.preview}
                  </div>

                  <h2 style={{
                    fontSize: '22px',
                    fontWeight: 'bold',
                    color: 'var(--text)',
                    margin: '0 0 12px 0',
                    textAlign: 'center'
                  }}>
                    {item.name}
                  </h2>

                  <p style={{
                    fontSize: '14px',
                    color: 'var(--text-secondary)',
                    margin: '0 0 20px 0',
                    textAlign: 'center',
                    lineHeight: '1.6'
                  }}>
                    {item.description}
                  </p>

                  <div style={{
                    background: 'var(--hover-bg)',
                    padding: '14px',
                    borderRadius: '12px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '28px',
                      fontWeight: 'bold',
                      color: getRarityColor(item.rarity)
                    }}>
                      {item.price} pts
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <button
                      onClick={() => setPreviewTheme({ id: item.id, name: item.name })}
                      style={{
                        flex: 1,
                        padding: '12px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        borderRadius: '10px',
                        border: '2px solid var(--border)',
                        cursor: 'pointer',
                        backgroundColor: 'var(--card-bg)',
                        color: 'var(--text)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--hover-bg)';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'var(--card-bg)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      👁️ Preview
                    </button>
                  </div>

                  {!isPurchased ? (
                    item.donatorsOnly ? (
                      <div style={{
                        width: '100%',
                        padding: '14px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        borderRadius: '12px',
                        border: '2px solid var(--warning-text)',
                        backgroundColor: 'var(--warning-bg)',
                        color: 'var(--warning-text)',
                        textAlign: 'center'
                      }}>
                        👑 DONATORS ONLY
                      </div>
                    ) : (
                      <button
                        onClick={() => handlePurchaseTheme(item.id, item.name, item.price)}
                        disabled={!canAfford || purchasing === item.id}
                        style={{
                          width: '100%',
                          padding: '14px',
                          fontSize: '15px',
                          fontWeight: 'bold',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: canAfford ? 'pointer' : 'not-allowed',
                          background: canAfford 
                            ? `linear-gradient(135deg, ${getRarityColor(item.rarity)} 0%, ${getRarityColor(item.rarity)}CC 100%)`
                            : 'var(--btn-secondary)',
                          color: 'white',
                          transition: 'all 0.3s ease',
                          opacity: purchasing === item.id ? 0.7 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (canAfford && purchasing !== item.id) {
                            e.target.style.transform = 'scale(1.05)';
                            e.target.style.boxShadow = `0 6px 20px ${getRarityColor(item.rarity)}66`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (canAfford) {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {purchasing === item.id ? '⏳ Purchasing...' : canAfford ? '🛒 Purchase' : '🔒 Insufficient Points'}
                      </button>
                    )
                  ) : (
                    <div style={{
                      width: '100%',
                      padding: '14px',
                      fontSize: '15px',
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      border: '2px solid var(--success-text)',
                      backgroundColor: 'var(--success-bg)',
                      color: 'var(--success-text)',
                      textAlign: 'center'
                    }}>
                      ✓ Theme Unlocked
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{
            marginTop: '60px',
            padding: '28px',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            borderRadius: '20px',
            border: '2px solid var(--border)'
          }}>
            <h3 style={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: 'var(--text)',
              marginBottom: '16px'
            }}>
              ℹ️ About Themes
            </h3>
            <ul style={{
              color: 'var(--text-secondary)',
              fontSize: '15px',
              lineHeight: '1.8',
              paddingLeft: '24px',
              margin: 0
            }}>
              <li>Themes change the entire website appearance</li>
              <li>Cat Paradise themes feature an interactive cat with eyes that follow your cursor!</li>
              <li>Change your active theme anytime in Settings</li>
              <li>Your points contribution to the leaderboard never decreases</li>
            </ul>
          </div>
        </>
      )}

      {/* Memberships Tab */}
      {activeTab === 'memberships' && (
        <div style={{ marginTop: '40px' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--text)', marginBottom: '16px' }}>
              ⭐ Support & Get Exclusive Benefits
            </h2>
            <p style={{ fontSize: '18px', color: 'var(--text-secondary)' }}>
              Help keep the platform running and get special perks
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '24px',
            marginBottom: '40px'
          }}>
            {[
      { tier: 'Bronze', icon: '🥉', price: 1, color: '#CD7F32', perks: ['Support the website monthly', 'Special Website Badge', 'Special mention on Hall of Fame page', '1.25x entries on giveaways', 'Earn 1.25x more website points', `Get access to exclusive supporter cursors`] },
      { tier: 'Silver', icon: '🥈', price: 2, color: '#C0C0C0', perks: ['Support the website monthly', 'Special Website Badge', 'Special mention on Hall of Fame page', '1.5x entries on giveaways', 'Earn 1.5x more website points', `Get access to exclusive supporter cursors`] },
      { tier: 'Gold', icon: '🥇', price: 3, color: '#FFD700', perks: ['Support the website monthly', 'Special Website Badge', 'Special mention on Hall of Fame page', '1.75x entries on giveaways', 'Earn 1.75x more website points', 'Special Cat Theme Website UI', `Get access to exclusive supporter cursors`], featured: true },
      { tier: 'Platinum', icon: '💎', price: 5, color: '#E5E4E2', perks: ['Support the website monthly', 'Special Website Badge', 'Special mention on Hall of Fame page', '2x entries on giveaways', 'Earn 2x more website points', 'Shoutout on website popup every 24 hours', 'Special Cat Theme Website UI', `Get access to exclusive supporter cursors, including your own custom cursor.`] }
    ].map(membership => (
      <div
        key={membership.tier}
        style={{
          backgroundColor: 'var(--card-bg)',
          borderRadius: '20px',
          padding: '28px',
          border: `3px solid ${membership.color}`,
          boxShadow: `0 8px 24px ${membership.color}40`,
          transition: 'transform 0.3s ease',
          transform: membership.featured ? 'scale(1.05)' : 'scale(1)'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = membership.featured ? 'translateY(-8px) scale(1.05)' : 'translateY(-8px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = membership.featured ? 'scale(1.05)' : 'scale(1)'}
      >
        <div style={{ fontSize: '56px', textAlign: 'center', marginBottom: '16px' }}>{membership.icon}</div>
        <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text)', textAlign: 'center', marginBottom: '12px' }}>
          {membership.tier} Supporter
        </h3>
        <div style={{ fontSize: '36px', fontWeight: 'bold', textAlign: 'center', color: membership.color, marginBottom: '8px' }}>
          ${membership.price}<span style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>/mo</span>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '2', marginBottom: '20px' }}>
          {membership.perks.map((perk, idx) => (
            <li key={idx}>✓ {perk}</li>
          ))}
        </ul>
        <a
          href="https://razorpay.me/@alonelycat"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            padding: '14px',
            fontSize: '16px',
            fontWeight: 'bold',
            textAlign: 'center',
            borderRadius: '12px',
            backgroundColor: membership.color,
            color: membership.tier === 'Gold' || membership.tier === 'Platinum' ? '#000' : 'white',
            textDecoration: 'none',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = `0 6px 20px ${membership.color}60`;
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = 'none';
          }}
        >
          Join Now
        </a>
      </div>
    ))}
          </div>
        </div>
      )}

      {/* Rewards Tab */}
      {activeTab === 'rewards' && (
        <div style={{ marginTop: '40px' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--text)', marginBottom: '16px' }}>
              🎁 Host Rewards Program
            </h2>
            <p style={{ fontSize: '18px', color: 'var(--text-secondary)' }}>
              Host 10 giveaways, earn 1000 points!
            </p>
          </div>

          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <div style={{
              backgroundColor: 'var(--card-bg)',
              borderRadius: '20px',
              padding: '40px',
              border: '3px solid #667eea',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#667eea', marginBottom: '8px' }}>
                    {giveawayStats.hosted || 0}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: '600' }}>
                    Giveaways Hosted
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#22c55e', marginBottom: '8px' }}>
                    {giveawayStats.claimed || 0}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: '600' }}>
                    Rewards Claimed
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Progress</span>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>
                    {((giveawayStats.hosted || 0) - (giveawayStats.claimed || 0) * 10)} / 10
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '16px',
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min(100, (((giveawayStats.hosted || 0) - (giveawayStats.claimed || 0) * 10) / 10) * 100)}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    transition: 'width 0.5s ease',
                    borderRadius: '8px'
                  }} />
                </div>
              </div>

              <button
                onClick={handleClaimGiveawayReward}
                disabled={!giveawayStats.canClaim || claiming}
                style={{
                  width: '100%',
                  padding: '18px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: giveawayStats.canClaim ? 'pointer' : 'not-allowed',
                  background: giveawayStats.canClaim 
                    ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' 
                    : 'var(--btn-secondary)',
                  color: 'white',
                  transition: 'all 0.3s ease',
                  opacity: claiming ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (giveawayStats.canClaim && !claiming) {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.boxShadow = '0 8px 24px rgba(34, 197, 94, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (giveawayStats.canClaim) {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {claiming ? '⏳ Claiming...' : giveawayStats.canClaim ? '🎉 Claim 1000 Points!' : `🔒 Host ${10 - ((giveawayStats.hosted || 0) - (giveawayStats.claimed || 0) * 10)} more`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Theme Preview Modal */}
      <ThemePreviewModal
        isOpen={previewTheme !== null}
        onClose={() => setPreviewTheme(null)}
        themeId={previewTheme?.id}
        themeName={previewTheme?.name}
      />
    </div>
  );
};

export default Shop;
